import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MetaData from '../../layouts/MetaData';
import RoleShell from '../../components/Enrollment/RoleShell';
import { ApprovedUnsentBanner } from '../../components/DeviceRequests/DeviceRequestBanners';
import { DR_API, DEVICE_TYPES, STATUS, STATUS_KEYS, NURSE_ROLES, REVIEW_ROLES, errMsg, fmtDate, fmtDateTime, patientLabel, typeLabel } from '../../components/DeviceRequests/deviceRequestApi';
import { useSortableTable, SortableTh, personName, serverSortParams } from '../../components/SortableTable';

const REQUEST_SORT_COLUMNS = {
    requestNo: { type: 'string', value: (r) => r?.requestNo },
    patient: { type: 'name', value: (r) => personName(r?.patient) },
    dob: { type: 'date', value: (r) => r?.patient?.DOB },
    emrId: { type: 'string', value: (r) => r?.patient?.emrId },
    organization: { type: 'string', value: (r) => r?.organization?.name },
    provider: { type: 'name', value: (r) => r?.providerName },
    prescribed: { type: 'date', value: (r) => r?.datePrescribed },
    status: { type: 'string', value: (r) => r?.status },
    submitted: { type: 'date', value: (r) => r?.submittedAt },
    approved: { type: 'date', value: (r) => r?.approvedAt },
    batch: { type: 'string', value: (r) => r?.batch?.batchNo },
    delivery: { type: 'string', value: (r) => r?.delivery?.method },
};
// keys the backend export.csv whitelist accepts (src/utils/listSort.js deviceRequests)
const EXPORT_SORT_KEYS = ['requestNo', 'patient', 'dob', 'emrId', 'organization', 'provider', 'prescribed', 'status', 'submitted', 'approved', 'batch'];
const BATCH_SORT_COLUMNS = {
    batchNo: { type: 'string', value: (b) => b?.batchNo },
    date: { type: 'date', value: (b) => b?.date || b?.createdAt },
    organization: { type: 'string', value: (b) => b?.organizationId?.name },
    sentBy: { type: 'name', value: (b) => b?.createdByName },
    count: { type: 'number', value: (b) => b?.count },
};

const QUEUE = 'SUBMITTED,CANNOT_FULFIL,RETURNED,APPROVED';

/** Nurse: my requests. Org Manager: review queue. Super Admin: all orgs + counts, filters, batch history, CSV. */
const DeviceRequests = () => {
    const { user } = useSelector((s) => s.auth);
    const role = user?.role;
    const isAdmin = role === 'admin';
    const isNurse = NURSE_ROLES.includes(role);
    const canReview = REVIEW_ROLES.includes(role);
    const location = useLocation();
    const history = useHistory();
    const qs = new URLSearchParams(location.search);
    const [filters, setFilters] = useState({
        status: qs.get('status') || (isAdmin ? '' : canReview ? QUEUE : ''),
        deviceType: '', organizationId: '', search: '', mine: isNurse,
    });
    const [data, setData] = useState({ requests: [], count: 0 });
    const [counts, setCounts] = useState({});
    const [orgs, setOrgs] = useState([]);
    const [batches, setBatches] = useState([]);
    const tableSort = useSortableTable(data?.requests, { id: 'deviceRequests.list', columns: REQUEST_SORT_COLUMNS, defaultSort: { key: 'patient', dir: 'asc' } });
    const batchSort = useSortableTable(batches, { id: 'deviceRequests.batches', columns: BATCH_SORT_COLUMNS, defaultSort: { key: 'date', dir: 'desc' } });
    const [tab, setTab] = useState('requests');
    const [error, setError] = useState('');

    const params = useCallback(() => {
        const p = {};
        Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
        return p;
    }, [filters]);

    const load = useCallback(async () => {
        setError('');
        try {
            const [list, sum] = await Promise.all([
                axios.get(DR_API, { params: { ...params(), limit: 200 } }),
                axios.get(`${DR_API}/summary`, { params: { ...params(), status: undefined } }),
            ]);
            setData(list.data);
            setCounts(sum.data || {});
        } catch (err) { setError(errMsg(err)); }
    }, [params]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!isAdmin && !canReview) return;
        axios.get(`${DR_API}/batches`).then((r) => setBatches(r.data.batches || [])).catch(() => {});
        if (isAdmin) axios.get(`${DR_API.replace('/device-requests', '')}/organizations/mine`)
            .then((r) => setOrgs(Array.isArray(r.data) ? r.data : r.data.organizations || r.data.results || [])).catch(() => {});
    }, [isAdmin, canReview]);

    const exportCsv = async () => {
        try {
            const res = await axios.get(`${DR_API}/export.csv`, { params: { ...params(), ...serverSortParams(tableSort, EXPORT_SORT_KEYS) }, responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `device-requests-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) { setError(errMsg(err)); }
    };
    const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const title = isAdmin ? 'Device Requests — all organizations' : canReview ? 'Device Requests — review queue' : 'My Device Requests';

    return (
        <RoleShell title="Device Requests">
            <MetaData title="Device Requests" />
            {canReview && <ApprovedUnsentBanner />}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">{title}</h5>
                <div>
                    {canReview && <Link className="btn btn-outline-primary btn-sm mr-2" to="/device-requests/daily">Daily Enrollment Request</Link>}
                    <Link className="btn btn-primary btn-sm mr-2" to="/device-requests/new">+ New RPM Device Request</Link>
                    {isAdmin && <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportCsv}>Export CSV</button>}
                </div>
            </div>

            <div className="mb-2" data-testid="dr-status-counts">
                <button type="button" className={`btn btn-sm mr-1 mb-1 ${!filters.status ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setFilters((f) => ({ ...f, status: '' }))}>
                    All {Object.values(counts).reduce((a, b) => a + b, 0)}
                </button>
                {STATUS_KEYS.map((s) => (
                    <button key={s} type="button" className={`btn btn-sm mr-1 mb-1 ${filters.status === s ? `btn-${STATUS[s].bg}` : `btn-outline-${STATUS[s].bg}`}`} onClick={() => setFilters((f) => ({ ...f, status: s }))}>
                        {STATUS[s].label} {counts[s] || 0}
                    </button>
                ))}
                {canReview && !isAdmin && (
                    <button type="button" className={`btn btn-sm mb-1 ${filters.status === QUEUE ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilters((f) => ({ ...f, status: QUEUE }))}>Open queue</button>
                )}
            </div>

            <div className="form-row mb-2">
                <div className="col-md-3"><input className="form-control form-control-sm" placeholder="Search patient / request #" value={filters.search} onChange={set('search')} /></div>
                <div className="col-md-2">
                    <select className="form-control form-control-sm" value={filters.deviceType} onChange={set('deviceType')} aria-label="Device type">
                        <option value="">All device types</option>
                        {DEVICE_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                </div>
                {isAdmin && (
                    <div className="col-md-3">
                        <select className="form-control form-control-sm" value={filters.organizationId} onChange={set('organizationId')} aria-label="Organization">
                            <option value="">All organizations</option>
                            {(Array.isArray(orgs) ? orgs : []).map((o) => <option key={o._id || o.id} value={o._id || o.id}>{o.name}</option>)}
                        </select>
                    </div>
                )}
                {isNurse && <div className="col-md-2"><label className="small"><input type="checkbox" checked={filters.mine} onChange={set('mine')} /> Only mine</label></div>}
                {(isAdmin || canReview) && (
                    <div className="col-md-2">
                        <button type="button" className={`btn btn-sm ${tab === 'batches' ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => setTab(tab === 'batches' ? 'requests' : 'batches')}>
                            {tab === 'batches' ? 'Show requests' : 'Batch history'}
                        </button>
                    </div>
                )}
            </div>
            {error && <div className="alert alert-danger py-2">{error}</div>}

            {tab === 'batches' ? (
                <table className="table table-sm table-striped" data-testid="dr-batches">
                    <thead><tr>
                        <SortableTh sortKey="batchNo" sort={batchSort}>Batch</SortableTh><SortableTh sortKey="date" sort={batchSort}>Date</SortableTh>
                        <SortableTh sortKey="organization" sort={batchSort}>Organization</SortableTh><SortableTh sortKey="sentBy" sort={batchSort}>Sent by</SortableTh>
                        <SortableTh sortKey="count" sort={batchSort}>Count</SortableTh><th>Emailed ECs</th><th />
                    </tr></thead>
                    <tbody>
                        {batchSort.rows.map((b) => (
                            <tr key={b._id}><td>{b.batchNo}</td><td>{b.date}</td><td>{b.organizationId?.name}</td><td>{b.createdByName}</td><td>{b.count}</td><td>{b.emailedTo}</td>
                                <td><Link to={`/device-requests/batches/${b._id}`}>Print view</Link></td></tr>
                        ))}
                        {!batches.length && <tr><td colSpan={7} className="text-muted">No batches yet.</td></tr>}
                    </tbody>
                </table>
            ) : (
                <table className="table table-sm table-striped table-hover" data-testid="dr-list">
                    <thead><tr>
                        <SortableTh sortKey="requestNo" sort={tableSort}>Request</SortableTh><SortableTh sortKey="patient" sort={tableSort}>Patient</SortableTh>
                        {isAdmin && <SortableTh sortKey="organization" sort={tableSort}>Organization</SortableTh>}<th>Device(s)</th>
                        <SortableTh sortKey="provider" sort={tableSort}>Provider</SortableTh><SortableTh sortKey="prescribed" sort={tableSort}>Prescribed</SortableTh>
                        <SortableTh sortKey="status" sort={tableSort}>Status</SortableTh><SortableTh sortKey="submitted" sort={tableSort}>Submitted</SortableTh>
                        <SortableTh sortKey="batch" sort={tableSort}>Batch</SortableTh>
                    </tr></thead>
                    <tbody>
                        {tableSort.rows.map((r) => (
                            <tr key={r._id} role="button" onClick={() => history.push(`/device-requests/${r._id}`)}>
                                <td>{r.requestNo}</td><td>{patientLabel(r.patient)}</td>{isAdmin && <td>{r.organization?.name}</td>}
                                <td>{(r.deviceTypes || []).map(typeLabel).join(', ')}</td><td>{r.providerName}</td><td>{fmtDate(r.datePrescribed)}</td>
                                <td><span className={`badge badge-${(STATUS[r.status] || {}).bg}`}>{(STATUS[r.status] || {}).label || r.status}</span>{r.status === 'RETURNED' && r.returnReason && <div className="small text-muted">{r.returnReason}</div>}</td>
                                <td>{fmtDateTime(r.submittedAt)}<div className="small text-muted">{r.createdByName}</div></td><td>{r.batch?.batchNo || ''}</td>
                            </tr>
                        ))}
                        {!data.requests.length && <tr><td colSpan={9} className="text-muted">No device requests.</td></tr>}
                    </tbody>
                </table>
            )}
        </RoleShell>
    );
};

export default DeviceRequests;
